import React, { useRef, useEffect } from "react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { LanguageCode } from "../api/contracts/language-settings";
import { useOfferedLanguages } from "../utils/useOfferedLanguages";

interface LanguageDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  selectedLanguage: LanguageCode;
  onLanguageChange: (language: LanguageCode) => void;
}

export const LanguageDropdown: React.FC<LanguageDropdownProps> = ({
  isOpen,
  onClose,
  selectedLanguage,
  onLanguageChange,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const languages = useOfferedLanguages();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={dropdownRef}
      className="absolute left-0 top-full z-50 mt-2 min-w-[160px] overflow-hidden rounded-3xl border border-border bg-surface-card shadow-lg transition-all duration-200"
    >
      <div className="flex flex-col gap-2 p-2">
        {languages.map((language) => (
          <button
            key={language}
            className={clsx(
              "flex w-full items-center gap-3 rounded-xl p-3 transition-colors duration-200",
              selectedLanguage === language ? "bg-ink/5" : "hover:bg-ink/5",
            )}
            onClick={() => {
              onLanguageChange(language);
              onClose();
            }}
          >
            <span className="text-sm">{t(`language.${language}`)}</span>
            {selectedLanguage === language && (
              <div className="ml-auto h-2 w-2 rounded-circle bg-primary" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
};
